import { Link } from 'react-router-dom'
import { Esqueleto } from '../../components/Estados'
import { IconeSeta } from '../../components/Icones'
import { cn } from '../../lib/cn'
import { contagemPorExtenso, type CartaoDoPainel, type Contagem } from './contagens'
import { enderecoDaLista } from './recortes'

/**
 * O número, ou o que a tela sabe no lugar dele.
 *
 * Três estados diferentes e três desenhos diferentes, porque confundi-los é o que faz alguém
 * tomar decisão com o número errado: carregando é espaço reservado, indisponível é travessão, e
 * zero é zero. Ver "0" e ser "ainda não sei" seria o pior dos três.
 *
 * @param props a contagem do recorte
 * @returns o número, o esqueleto ou o travessão
 */
function Numero({ contagem }: { contagem: Contagem }) {
  if (contagem.carregando) {
    return <Esqueleto linhas={1} className="mt-3 w-16" />
  }

  if (contagem.quantidade === null) {
    return (
      <p className="mt-2 text-3xl font-bold text-slate-400">
        —<span className="sr-only">não foi possível contar agora</span>
      </p>
    )
  }

  return (
    <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">
      {contagemPorExtenso(contagem.quantidade)}
    </p>
  )
}

/**
 * Um recorte do processo, com a contagem e o caminho para a lista correspondente.
 *
 * O cartão inteiro é UM link, e não uma caixa com um link dentro: o alvo de clique passa a ser o
 * cartão todo, o leitor de tela anuncia um destino só e o "abrir em nova aba" continua valendo.
 * Por isso nada aqui dentro pode virar botão.
 *
 * O destino leva o recorte na query string, procedência inclusive. É o que impede a pergunta que
 * derruba a confiança no painel: clicar num cartão que diz 4 e cair numa lista que diz 9.
 *
 * @param props o recorte e a contagem dele
 * @returns o cartão
 */
export function CartaoDeContagem({ recorte, contagem }: CartaoDoPainel) {
  return (
    <li>
      <Link
        to={enderecoDaLista(recorte)}
        className={cn(
          'group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-cartao',
          'transition-colors hover:border-niteroi-200 hover:bg-niteroi-50',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          'focus-visible:outline-niteroi-600',
        )}
      >
        {/* Preenchimento sem texto em cima: é onde a cor viva da paleta cabe sem problema de
            contraste, e é o que liga o cartão à etiqueta do mesmo estado na listagem. */}
        <span aria-hidden="true" className={cn('h-1.5 w-10 rounded-full', recorte.acento)} />
        <span className="mt-3 text-sm font-medium text-slate-700">{recorte.rotulo}</span>
        <Numero contagem={contagem} />
        <span className="mt-2 text-xs leading-relaxed text-slate-500">{recorte.ajuda}</span>
        {/* `mt-auto` empurra a chamada para o rodapé do cartão: com textos de ajuda de tamanhos
            diferentes, sem isso a linha "Ver a lista" fica numa altura em cada cartão da grade. */}
        <span className="mt-auto flex items-center gap-1 pt-4 text-xs font-semibold text-niteroi-700">
          Ver a lista
          <IconeSeta className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    </li>
  )
}
